import axios from 'axios';
import md5 from 'md5';
import _ from 'lodash';
import RESPONSE from '@/constants/response';
import app from '@/main';

const baseHeader = {
  headers: {
    Accept: '*/*',
  },
  timeout: 10000,
};

function successReturn(data) {
  // 예외 처리.
  if (data.resultCode && data.resultCode !== RESPONSE.SUCCESS.CODE) {
    return {
      status: RESPONSE.FAIL.CODE,
      error: {
        resultCode: data.resultCode,
        resultMsg: data.resultMsg,
      },
    };
  }
  return {
    status: RESPONSE.SUCCESS.CODE,
    data,
  };
}

function errorReturn(error) {
  if (error.response && error.response.status) {
    if (error.response.data.resultMsg) {
      return {
        status: error.response.status,
        error: {
          resultCode: error.response.data.resultCode,
          resultMsg: error.response.data.resultMsg,
        },
      };
    }
    return {
      status: error.response.status,
      error: {
        resultCode: error.response.status,
        resultMsg: error.response.data.error_description,
      },
    };
  }
  return {
    status: RESPONSE.FAIL.CODE,
    data: {
      resultCode: RESPONSE.FAIL.CODE,
      resultMsg: app.$t('error.undefined'),
    },
  };
}

function httpDataGet(url, params) {
  const apiParams = {
    baseHeader,
    params,
  };

  return axios.get(
    url,
    apiParams,
  )
    .then(({ data }) => successReturn(data))
    .catch(error => errorReturn(error));
}

function httpDataPost(url, params) {
  return axios.post(url, params, baseHeader)
    .then(({ data }) => successReturn(data))
    .catch(error => errorReturn(error));
}

function httpDataDelete(url, params) {
  return axios.delete(url, params)
    .then(({ data }) => successReturn(data))
    .catch(error => errorReturn(error));
}

function oauthPost(commit, formData) {
  const originData = `${process.env.CLIENT_ID}:${process.env.CLIENT_SECRET}`;
  const encodeData = btoa(originData);
  return axios.post('/oauth/token', formData,
    {
      timeout: 10000,
      headers: {
        Authorization: `Basic ${encodeData}`,
        Accept: '*/*',
      },
    })
    .then(({ data }) => {
      commit('logIn', {
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        userSeqId: data.userSeqId,
        userName: data.name,
        userEmail: data.userId,
        profileImage: data.profileImage,
      });

      // 모든 HTTP 요청 헤더에 Authorization 을 추가한다.
      axios.defaults.headers.common.Authorization = `Bearer ${data.access_token}`;

      return successReturn(data);
    })
    .catch(error => errorReturn(error));
}

export default {

  addTag({ commit }, tag) {
    if (!_.isEmpty(tag)) {
      commit('addTag', tag);
    }
  },
  delTag({ commit }, idx) {
    commit('delTag', idx);
  },
  copyTagList({ commit }) {
    commit('copyTagList');
  },
  toggleMenu({ commit }, isShow) {
    commit('toggleMenu', isShow);
  },

  /**
   * Login
   *
   * @param commit
   * @param {string} email - user id
   * @param {string} password - user password
   * @returns {Promise<{data: *}>}
   */
  logIn({ commit }, { email, password }) {
    const formData = new FormData();
    formData.append('username', email);
    formData.append('password', md5(password));
    formData.append('grant_type', 'password');

    return oauthPost(commit, formData);
  },

  /**
   * Logout
   *
   * @param commit
   */
  logOut({ commit }) {
    axios.defaults.headers.common.Authorization = undefined;
    commit('logOut');
  },

  /**
   * 문제 조회.
   *
   * @param getters
   * @returns {*}
   */
  getQuestion({ getters }) {
    const params = {
      lang: getters.getLanguage.toUpperCase(),
    };

    return httpDataGet(
      `/api/v2.1/${getters.getUserSeqId}/question`,
      params,
    );
  },

  /**
   * 내 보고서
   *
   * @param getters - getUserSeqId
   * @returns {Promise<{status: number, data} | {status: *, data: *}>}
   */
  getMyResult({ getters }) {
    const url = `/api/v2.0/${getters.getUserSeqId}/myresult`;
    const params = {
      lang: getters.getLanguage.toUpperCase(),
    };

    return httpDataGet(url, params);
  },

  /**
   * 내 팀역할 데이터 가져오기
   *
   * @param getters
   * @returns {Promise<{
   *   data: {ideation, multiplier, solution, judgement, execution, critic}
   *   | {status: *, data: *}
   * >}
   */
  getMyTeamRole({ getters }) {
    const url = `/api/v1.0/${getters.getUserSeqId}/myteamresult?appVersion=2`;

    return httpDataGet(url);
  },

  /**
   * 언어 변경.
   *
   * @param context
   * @param payload
   */
  setI18n(context, payload) {
    context.commit('setI18n', payload);
  },

  /**
   * 프로필 가져오기
   *
   * @param context
   * @param userSeqId
   * @returns {Promise<{status: number, data} | {status: *, data: *}>}
   */
  getProfile(context, { userSeqId }) {
    const url = `/api/v2.0/users/${userSeqId}`;

    return httpDataGet(url);
  },

  /**
   * 프로필 수정
   *
   * @param getters - getUserSeqId
   * @param data{name, nickName, email, birth, education}
   * @returns {Promise<AxiosResponse<any> | {status: *, data: *}>}
   */
  editProfile({ getters }, { data }) {
    const url = `/api/v2.0/users/${getters.getUserSeqId}`;

    const formData = new FormData();
    formData.append('name', data.name);
    formData.append('nickname', data.nickname);
    formData.append('email', data.email);
    formData.append('birth', data.birth);
    formData.append('education', data.education);
    formData.append('image', data.image);
    formData.append('jobs', JSON.stringify(data.jobs));

    return httpDataPost(url, formData);
  },

  /**
   * 알림 목록 가져오기
   *
   */
  getNotificationList() {
    return httpDataGet('/api/v2.0/group/alarms');
  },

  /**
   * 매칭 리스트
   *
   * @param getters - getUserSeqId
   * @returns {
   * Promise<{
   *   status: number,
   *   data[{matchusername, matchuserseqid, modifydate, mrid, regdate, result,
   *         status, username, userseqid}]}
   * | {status: *, data: *}>
   * }
   */
  getMatchList({ getters }) {
    const url = `/api/v2.0/${getters.getUserSeqId}/match/list`;

    return httpDataGet(url);
  },

  /**
   *
   * @param getters - getUserSeqId
   * @param groupId - 그룹 ID
   */
  getMatchListForInvite({ getters }, { groupId }) {
    return httpDataGet(
      `/api/v2.0/${getters.getUserSeqId}/match/listToAddGroup?groupId=${groupId}`,
    );
  },

  /**
   * 그룹 리스트
   */
  getGroupList() {
    return httpDataGet('/api/v2.0/groups');
  },

  /**
   * 그룹 멤버 리스트
   *
   * @param groupId - 그룹 ID
   */
  getGroupMemberList(context, { groupId }) {
    return httpDataGet(`/api/v2.0/groups/detail?groupId=${groupId}`);
  },

  /**
   * 그룹 생성
   *
   * @param groupName - 생성할 그룹 이름
   */
  createGroup(context, { groupName }) {
    const params = {
      name: groupName,
    };
    return httpDataPost('/api/v2.0/groups', params);
  },

  /**
   * 그룹 삭제
   *
   * @param groupId - 그룹 ID
   */
  removeGroup(context, { groupId }) {
    return httpDataPost(`/api/v2.0/groups/delete/g${groupId}`);
  },

  /**
   * 그룹 초대
   *
   * @param groupId - 그룹 ID
   * @param members - 초대할 친구들 UserSeqId
   */
  inviteMember(context, { groupId, members }) {
    const params = {
      members,
    };
    return httpDataPost(`/api/v2.0/groups/invite/g${groupId}`, params);
  },

  /**
   * 멤버 추가 (초대 수락)
   *
   * @param groupId - 그룹 ID
   * @param alarmId - 알림 ID
   * @param userId - 초대할 친구 UserSeqId
   */
  addMember(context, { groupId, alarmId, userId }) {
    const params = {
      groupId,
      alarmId,
      userId,
    };
    return httpDataPost('/api/v2.0/group/members/add', params);
  },

  addMemberImmediately(context, { groupId }) {
    const params = {
      groupId,
    };
    return httpDataGet('/api/v2.0/group/members/directAdd', params);
  },

  /**
   * 초대 거절
   *
   * @param alarmId - 알림 ID
   */
  disagreeInvite(context, { alarmId }) {
    return httpDataGet(`/api/v2.0/group/members/deny?alarmId=${alarmId}`);
  },

  /**
   * 그룹 멤버 삭제
   *
   * @param groupId - 그룹 ID
   * @param members - 삭제할 친구들 UserSeqId
   */
  expelMember(context, { groupId, members }) {
    const params = {
      groupId,
      members,
    };
    return httpDataPost('/api/v2.0/group/members/remove', params);
  },

  /**
   * 그룹장 위임
   *
   * @param groupId - 그룹 ID
   * @param userId - 그룹장 위임 받을 사용자 UserSeqId
   */
  delegateLeader(context, { groupId, userId }) {
    const params = {
      groupId,
      userId,
    };
    return httpDataPost('/api/v2.0/group/members/delegate', params);
  },

  /**
   * 그룹 초대 Without URL (QR)
   *
   * @param qrURL - QR URL
   */
  qrInviteMember(context, { groupId, senderId, userToAdd }) {
    const params = {
      groupId,
      senderId,
      userToAdd,
    };
    return httpDataGet('/api/v2.0/group/members/directAdd', params);
  },

  /**
   * 그룹 초대 With URL (QR)
   *
   * @param qrURL - QR URL
   */
  qrInviteMemberWithURL(context, { code }) {
    return axios
      .get(code)
      .then(({ data }) => ({
        status: RESPONSE.SUCCESS.CODE,
        data,
      }))
      .catch(error => ({
        status: error.response.status,
        data: error.response.data,
      }));
  },

  /**
   * 매칭 결과
   *
   * @param getters - getUserSeqId
   * @param matchUser - 상대방 userSeqId
   * @returns {
   *   Promise<{status: number, data{A, B, L, P, R, S, Total}}
   *   | {status: *, data: *}>
   * }
   */
  getMatchResult({ getters }, { matchUser }) {
    const url = `/api/v1.0/${getters.getUserSeqId}/match/result/${matchUser}`;
    const params = {
      lang: getters.getLanguage.toUpperCase(),
    };

    return httpDataGet(url, params);
  },
  /**
   * QR 이미지 가지고 오기
   * @param getters - getUserSeqId
   * @returns 바이러리 코드 }
   */
  getQRCode({ getters }) {
    const url = `/api/v1.0/${getters.getUserSeqId}/match/qrcode`;

    return httpDataGet(url);
  },

  /**
   * Group QR 이미지 가지고 오기
   * @param groupId - 그룹 ID
   * @returns Binary code
   */
  getGroupQRCode(context, { groupId }) {
    return httpDataGet(`/api/v2.0/groups/qrcode/g${groupId}`);
  },

  matchAdd(context, { url }) {
    return axios.post(url)
      .then(({ data }) => ({
        status: RESPONSE.SUCCESS.CODE,
        data,
      }))
      .catch(error => ({
        status: error.response.status,
        data: error.response.data,
      }));
  },

  /**
   * 회원 탈퇴
   *
   * @param getters - getUserSeqId
   * @param password - 비밀번호
   * @returns {Promise<{status: number, data} | {status: *, data: *}>}
   */
  deleteUser({ getters }, { password }) {
    const url = `/api/v1.0/user/${getters.getUserSeqId}`;

    return axios.delete(url, {
      data: {
        password: md5(password),
      },
    })
      .then(({ data }) => ({
        status: RESPONSE.SUCCESS.CODE,
        data,
      }))
      .catch(error => ({
        status: error.response.status,
        data: error.response.data,
      }));
  },

  checkUserId(context, { userId }) {
    const params = {
      userId,
    };

    return httpDataGet(
      '/common/api/v2.0/check/id',
      params,
    );
  },

  checkNickname(context, { nickname }) {
    const params = {
      nickname,
    };

    return httpDataGet(
      '/common/api/v2.0/check/nickname',
      params,
    );
  },

  /**
   * 직종 조회.
   *
   * @returns {*}
   */
  getJobs({ commit, getters }) {
    const apiParams = {
      baseHeader,
      lang: getters.getLanguage.toUpperCase(),
    };

    return axios.get('/api/v2.0/jobs', apiParams)
      .then(({ data }) => {
        commit('setJobs', data);
        return successReturn(data);
      })
      .catch(error => errorReturn(error));
  },

  setUserEmail({ commit }, payload) {
    commit('setUserEmail', payload);
  },

  /**
   * SNS Login
   *
   * @param context
   * @param payload
   * @returns {*}
   */
  getClientInfo(context, payload) {
    const params = {
      clientId: payload.clientId,
    };

    return httpDataGet(
      '/api/v2.0/users/clientInfo',
      params,
    );
  },

  /**
   * Sign up
   *
   * @param context
   * @param payload
   * @returns {*}
   */
  signUp(context, payload) {
    const formData = new FormData();
    formData.append('userId', payload.email);
    formData.append('password', md5(payload.password));
    formData.append('nickname', payload.nickname);
    formData.append('name', payload.name);
    formData.append('birth', payload.birth);
    formData.append('gender', payload.sexValue);
    formData.append('education', payload.education);
    formData.append('loginType', payload.loginType);
    formData.append('jobs', JSON.stringify(payload.jobs));
    formData.append('clientId', payload.clientId);

    return httpDataPost(
      '/api/v2.0/users',
      formData,
    );
  },

  /**
   * SNS Login
   *
   * @param context
   * @param payload
   * @returns {*}
   */
  checkSnsUserId(context, payload) {
    const formData = new FormData();
    formData.append('userId', payload.userId);
    formData.append('password', md5(payload.password));

    return httpDataPost('/common/api/v2.0/check/id/sns', formData);
  },

  /**
   * 테스트 응답 제출
   *
   * @param getters
   * @param data
   * @returns {*}
   */
  postQuestion({ getters }, { data }) {
    return httpDataPost(
      `/api/v2.1/${getters.getUserSeqId}/question`,
      data,
    );
  },

  /**
   * 읽지 않은 알림 조회
   *
   */
  getUnreadNotiCount() {
    return httpDataGet('/api/v2.0/group/alarms/unReadCount');
  },

  /**
   * 친구 삭제
   *
   * @param getters - getUserSeqId
   * @param payload - 친구 유저 아이디
   * @returns {*}
   */
  deleteMatchUser({ getters }, payload) {
    const url = `/api/v2.0/${getters.getUserSeqId}/match/list`;

    const param = {
      params: {
        matchUserSeqId: payload,
      },
    };

    return httpDataDelete(url, param);
  },

  /**
   * 비밀번호 재설정 메일 보내기
   *
   * @param context
   * @param email
   * @returns {*}
   */
  sendPwEmail(context, { email }) {
    return httpDataGet(
      'common/resetpw',
      email,
    );
  },

  /**
   * 네이버 엑세스 토큰 가져오기
   *
   * @param context
   * @param data
   * @returns {*}
   */
  naverAuth(context, { data }) {
    const params = {
      code: data.code,
      state: data.state,
    };

    return httpDataGet('/oauth/naver', params);
  },

  /**
   * 네이버 프로필 가져오기
   *
   * @param context
   * @param tokenData
   * @returns {*}
   */
  getNaverProfile(context, { tokenData }) {
    const params = {
      tokenType: tokenData.tokenType,
      accessToken: tokenData.accessToken,
    };

    return httpDataGet('naver/profile', params);
  },

  /**
   * 네이버 연결 해제
   *
   * @param context
   * @returns {*}
   */
  naverDisconnect(context, payload) { // eslint-disable-line no-unused-vars
    const url = '/naver/auth/cancel';
    const param = {
      params: {
        accessToken: payload,
      },
    };
    return httpDataDelete(url, param);
  },

  /**
   * SNS 로그인
   *
   * @param context
   * @param payload
   * @returns {*}
   */
  signUpSns(context, payload) {
    const formData = new FormData();
    formData.append('userId', payload.email);
    formData.append('password', md5(payload.password));
    formData.append('nickname', payload.nickname);
    formData.append('name', payload.name);
    formData.append('birth', payload.birth);
    formData.append('gender', payload.sexValue);
    formData.append('education', payload.education);
    formData.append('jobs', JSON.stringify(payload.jobs));
    formData.append('loginType', payload.loginType);
    formData.append('image', payload.profileImage);

    return httpDataPost('/api/v2.0/users/sns', formData);
  },

  refreshToken({ commit, getters }) {
    const formData = new FormData();
    formData.append('refresh_token', getters.getRefreshToken);
    formData.append('grant_type', 'refresh_token');

    return oauthPost(commit, formData);
  },

  getMc6({ getters }) {
    const params = {
      lang: getters.getLanguage.toUpperCase(),
    };
    return httpDataGet('/api/v2.0/mc6', params);
  },

  getMc6Detail(context, payload) {
    return httpDataGet(`/api/v2.0/mc6/${payload.type}`);
  },

  getShareMc6(context, payload) {
    const formData = new FormData();
    formData.append('userSeqId', payload.id);

    return httpDataPost('/api/v2.0/mc6', formData);
  },

  getMc6List({ getters }, payload) {
    const params = {
      type: payload.type,
    };
    return httpDataGet(`/api/v2.0/${getters.getUserSeqId}/match/list`, params);
  },

  addFriend({ getters }, payload) {
    return httpDataPost(`/api/v1.0/${getters.getUserSeqId}/match/${payload.friendSeqId}`, null);
  },

  getNoticeList(context) { // eslint-disable-line no-unused-vars
    return httpDataGet('/api/v2.0/bbs/bbsList', null);
  },

  getNoticeItem(context, { bbsId }) {
    return httpDataGet(`/api/v2.0/bbs/${bbsId}`, null);
  },

  // 회원가입 시, 사용하는 임시 데이터.
  setTmpUserEmail({ commit }, payload) {
    commit('setTmpUserEmail', payload);
  },
  setTmpUserPassword({ commit }, payload) {
    commit('setTmpUserPassword', payload);
  },
  setTmpUserNickname({ commit }, payload) {
    commit('setTmpUserNickname', payload);
  },
  setTmpUserName({ commit }, payload) {
    commit('setTmpUserName', payload);
  },
  setTmpUserBirth({ commit }, payload) {
    commit('setTmpUserBirth', payload);
  },
  setTmpUserSex({ commit }, payload) {
    commit('setTmpUserSex', payload);
  },
  setTmpUserEducation({ commit }, payload) {
    commit('setTmpUserEducation', payload);
  },
  setTmpValidEmailFlag({ commit }, payload) {
    commit('setTmpValidEmailFlag', payload);
  },
  setTmpValidPasswordFlag({ commit }, payload) {
    commit('setTmpValidPasswordFlag', payload);
  },
  setTmpValidNicknameFlag({ commit }, payload) {
    commit('setTmpValidNicknameFlag', payload);
  },
  setTmpValidNameFlag({ commit }, payload) {
    commit('setTmpValidNameFlag', payload);
  },
  clearTmpUserEmail({ commit }) {
    commit('clearTmpUserEmail');
  },
  clearTmpUserPassword({ commit }) {
    commit('clearTmpUserPassword');
  },
  clearTmpUserNickname({ commit }) {
    commit('clearTmpUserNickname');
  },
  clearTmpUserName({ commit }) {
    commit('clearTmpUserName');
  },
  clearTmpUserBirth({ commit }) {
    commit('clearTmpUserBirth');
  },
  clearTmpUserSex({ commit }) {
    commit('clearTmpUserSex');
  },
  clearTmpUserEducation({ commit }) {
    commit('clearTmpUserEducation');
  },
  clearTmpValidEmailFlag({ commit }) {
    commit('clearTmpValidEmailFlag');
  },
  clearTmpValidPasswordFlag({ commit }) {
    commit('clearTmpValidPasswordFlag');
  },
  clearTmpValidNicknameFlag({ commit }) {
    commit('clearTmpValidNicknameFlag');
  },
  clearTmpValidNameFlag({ commit }) {
    commit('clearTmpValidNameFlag');
  },
  addTmpTag({ commit }, tag) {
    commit('addTmpTag', tag);
  },
  delTmpTag({ commit }, idx) {
    commit('delTmpTag', idx);
  },
  copyTmpTagList({ commit }) {
    commit('copyTmpTagList');
  },
  clearTmpTagList({ commit }) {
    commit('clearTmpTagList');
  },
  setQCnt({ commit }, { qCnt }) {
    localStorage.qCnt = qCnt;
    commit('setQCnt', qCnt);
  },
  setTmpProfileImage({ commit }, url) {
    commit('setTmpProfileImage', url);
  },
  setProfileImage({ commit }, url) {
    commit('setProfileImage', url);
  },
  setTmpClientId({ commit }, clientId) {
    commit('setTmpClientId', clientId);
  },
  clearTmpClientId({ commit }) {
    commit('clearTmpClientId');
  },
};




// WEBPACK FOOTER //
// ./src/store/actions.js